import { ArrowRightOutlined, CloseOutlined, CloudOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import openCloud, { CLOUD_URL } from 'library/helpers/openCloud';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Flex, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

// The new cloud dashboard users should migrate to.
export const NEW_PLATFORM_URL = CLOUD_URL;
const DISMISS_KEY = 'zezamii.newPlatformBanner.dismissed';

// A few small, faint bubbles tucked into the bottom-right corner so they echo
// the login screen without crowding the message.
const CORNER_BUBBLES = [
	{ size: 10, right: '6%', bottom: '20%', opacity: 0.35, delay: '0s' },
	{ size: 18, right: '13%', bottom: '4%', opacity: 0.26, delay: '1.4s' },
	{ size: 8, right: '24%', bottom: '26%', opacity: 0.4, delay: '2.4s' },
	{ size: 24, right: '3%', bottom: '-8%', opacity: 0.2, delay: '0.8s' },
	{ size: 12, right: '30%', bottom: '6%', opacity: 0.3, delay: '3s' }
];

// Signin variant: reuses the login screen's exact backdrop
// (linear-gradient(to bottom, #1D3557, #060b11)) so it feels native to the
// "Know your Space. Know your People." world.
const SigninBanner = styled.div`
	position: relative;
	overflow: hidden;
	width: 100%;
	border-radius: ${({ $radius }) => $radius}px;
	background: linear-gradient(to bottom, #1d3557 0%, #060b11 100%);
	box-shadow: ${({ $shadow }) => $shadow};

	.bubbleLayer {
		position: absolute;
		inset: 0;
		z-index: 0;
		overflow: hidden;
		pointer-events: none;
	}
	.bubble {
		position: absolute;
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px solid rgba(255, 255, 255, 0.25);
		border-radius: 50%;
		animation: floatUp 14s ease-in-out infinite;
	}
	.bannerContent {
		position: relative;
		z-index: 1;
	}
	@keyframes floatUp {
		0%,
		100% {
			transform: translateY(0) scale(1);
		}
		50% {
			transform: translateY(-16px) scale(1.05);
		}
	}
`;

const readDismissed = () => {
	try {
		return window.localStorage.getItem(DISMISS_KEY) === '1';
	} catch (e) {
		return false;
	}
};

// Hands off the current Firebase session so cloud auto-logs the user in
// (topbar variant). On the login screen there is no user yet, so openCloud
// falls back to a plain open.
const openNewPlatform = () => openCloud();

// Per-product accents so the strip matches the dashboard it sits in
// (Access → brand blue #252B6F, Lockers → brand green #059641).
const ACCENTS = {
	blue: { gradient: 'linear-gradient(90deg, #252B6F 0%, #3A41A0 100%)', text: '#252B6F' },
	green: { gradient: 'linear-gradient(90deg, #037A34 0%, #059641 100%)', text: '#037A34' }
};

/**
 * Announcement inviting users to move to the new cloud dashboard (cloud.zezamii.com).
 *
 * - variant='topbar'  → slim, dismissible gradient strip (remembers dismissal in localStorage)
 * - variant='signin'  → bubble backdrop card (matches the login screen) shown above the login form
 * - accent='blue'|'green' → tints the topbar strip to the host dashboard's brand color
 */
const NewPlatformBanner = ({ variant = 'topbar', view, accent = 'blue' }) => {
	const {
		token: { paddingXS, paddingSM, paddingLG, borderRadiusLG, fontSizeSM, boxShadowSecondary }
	} = theme.useToken();

	const [dismissed, setDismissed] = useState(variant === 'topbar' ? readDismissed : false);
	const isMobile = view === 'MobileView';

	if (dismissed) return null;

	const dismiss = () => {
		try {
			window.localStorage.setItem(DISMISS_KEY, '1');
		} catch (e) {
			/* ignore storage failures */
		}
		setDismissed(true);
	};

	// Signin card keeps the brand-blue world of the login screen; the topbar
	// strip follows its host dashboard's accent (blue for Access, green for Lockers).
	const brandBlue = '#252B6F';
	const { gradient: topbarGradient, text: topbarBtnText } = ACCENTS[accent] || ACCENTS.blue;

	if (variant === 'signin') {
		return (
			<SigninBanner $radius={borderRadiusLG} $shadow={boxShadowSecondary} style={{ marginBottom: 32 }}>
				<div className='bubbleLayer'>
					{CORNER_BUBBLES.map((b, i) => (
						<span
							key={i}
							className='bubble'
							style={{
								width: b.size,
								height: b.size,
								right: b.right,
								bottom: b.bottom,
								opacity: b.opacity,
								animationDelay: b.delay
							}}
						/>
					))}
				</div>
				<div className='bannerContent' style={{ padding: paddingLG }}>
					<Flex vertical gap={paddingXS}>
						<Flex align='center' gap={paddingXS}>
							<CloudOutlined style={{ color: '#FFFFFF', fontSize: 22 }} />
							<Title level={4} style={{ color: '#FFFFFF', margin: 0 }}>
								Zezamii has a new home
							</Title>
						</Flex>
						<Text style={{ color: 'rgba(255,255,255,0.85)' }}>
							We&apos;ve launched a new cloud dashboard at <b>cloud.zezamii.com</b>. Please sign in there
							from now on for the latest experience.
						</Text>
						<Button
							size='large'
							onClick={openNewPlatform}
							style={{
								marginTop: paddingXS,
								background: '#FFFFFF',
								color: brandBlue,
								fontWeight: 600,
								border: 'none'
							}}
						>
							Go to cloud.zezamii.com <ArrowRightOutlined />
						</Button>
					</Flex>
				</div>
			</SigninBanner>
		);
	}

	// topbar variant — slim brand-blue gradient strip
	return (
		<Flex
			align='center'
			justify='space-between'
			gap={paddingSM}
			style={{
				background: topbarGradient,
				padding: `${paddingXS}px ${isMobile ? '20px' : '32px'}`
			}}
		>
			<Flex align='center' gap={paddingXS} style={{ minWidth: 0 }}>
				<CloudOutlined style={{ color: '#FFFFFF', fontSize: 16, flexShrink: 0 }} />
				<Text ellipsis style={{ color: '#FFFFFF', fontSize: fontSizeSM }}>
					{isMobile ? (
						<>We&apos;ve moved to the cloud.</>
					) : (
						<>
							We&apos;ve moved to the cloud — sign in to the new Zezamii dashboard at{' '}
							<b>cloud.zezamii.com</b>.
						</>
					)}
				</Text>
			</Flex>

			<Flex align='center' gap={paddingXS} style={{ flexShrink: 0 }}>
				<Button
					size='small'
					onClick={openNewPlatform}
					style={{ background: '#FFFFFF', color: topbarBtnText, fontWeight: 600, border: 'none' }}
				>
					Open new dashboard <ArrowRightOutlined />
				</Button>
				<Button
					type='text'
					size='small'
					aria-label='Dismiss'
					onClick={dismiss}
					icon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.9)' }} />}
				/>
			</Flex>
		</Flex>
	);
};

export default NewPlatformBanner;
